import { getLogger } from '@foundation-player/logger';
import { CanAutoPlayResult } from './can-autoplay';

/**
 * Autoplay detection is performed for each platform individually
 * to avoid side effects when optimizing detection for a single platform.
 */
export abstract class AutoplayDetection {
  protected readonly log = getLogger('player.core.AutoplayDetection');

  /**
   * It's recommended to execute the autoplay detection only once, before loading the actual video.
   *
   * Note:
   * - The `preload` attribute is ignored if autoplay is present.
   *
   * WARNING:
   * - If the autoplay attribute is set on the same element as for the content, the video may unintentionally start before the PreRoll slot.
   */
  async isAutoplaySupported(videoElement: HTMLVideoElement | undefined, isAutoplayEnabled: boolean, isMuted: boolean) {
    // If autoplay is disabled there is no need to check whether autoplay is supported
    if (!isAutoplayEnabled) {
      this.log.debug('Autoplay is disabled by config');
      return isAutoplayEnabled;
    }

    if (isMuted) {
      this.log.warn('Autoplay detection is performed on a muted video element.');
    }

    const { result: isAutoplaySupported, error } = await this.isAutoplaySupportedForPlatform(videoElement, isMuted);

    this.log.debug('Media autoplay', {
      config: { isAutoplayEnabled, isMuted },
      result: { isAutoplaySupported, error },
    });

    return isAutoplaySupported;
  }

  protected abstract isAutoplaySupportedForPlatform(
    videoElement: HTMLVideoElement | undefined,
    isMuted: boolean,
  ): Promise<CanAutoPlayResult>;
}
